<template>
  <!-- MBR055 Top-up — member_app_UI/song_fa_members_wallet_top_up_mobile -->
  <div class="page-header align-items-start min-vh-100 mbr-topup">
    <span class="mask bg-white"></span>
    <div class="container p-0">
      <!-- MBR056 header: back + title, History still opens My Transactions -->
      <header class="mbr-topup__bar">
        <div class="mbr-topup__head">
          <button type="button" class="mbr-topup__back" :aria-label="$t('common.back')" @click="goBack()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 class="mbr-topup__title">{{ $t('topup.title') }}</h1>
        </div>
        <button type="button" class="mbr-topup__history" @click="gotoTransaction()">{{ $t('topup.history') }}</button>
      </header>

      <main class="mbr-topup__body pb-90" v-if="isPageReady">
        <!-- MBR110 Wallet Balance in the Activity card style: same amount_in_account value -->
        <MbrWalletCard :label="$t('topup.wallet_balance')" :amount="'$ ' + formatMoney(cashbackAmount)" />

        <!-- MBR056 package grid; nothing is pre-selected (same as before) -->
        <!-- <p>, not <h2>: Material Dashboard sizes headings -->
        <p class="mbr-topup__eyebrow mbr-topup__section">{{ $t('topup.choose_package') }}</p>
        <div class="mbr-topup__grid" role="radiogroup" :aria-label="$t('topup.packages_aria')">
          <button
            v-for="(price, index) in prices"
            :key="index"
            type="button"
            role="radio"
            class="mbr-topup__pkg"
            :class="{ 'mbr-topup__pkg--on': currentPrice === price }"
            :aria-checked="currentPrice === price ? 'true' : 'false'"
            @click="selectPrice(index, price)"
          >
            <span v-if="currentPrice === price" class="mbr-topup__check" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            <span class="mbr-topup__get">{{ $t('topup.get_amount', { amount: '$' + formatShort(price.topup_amount) }) }}</span>
            <span class="mbr-topup__pay">{{ $t('topup.pay_amount', { amount: '$' + formatShort(price.payment_amount) }) }}</span>
            <span v-if="bonusOf(price) > 0" class="mbr-topup__bonus">
              {{ $t('topup.bonus', { amount: '$' + formatShort(bonusOf(price)) }) }}
            </span>
          </button>
        </div>

        <!-- MBR057 one-line consent; the T&C HTML moved into a sheet -->
        <i18n-t v-if="terms_conditions" keypath="topup.terms_consent" tag="p" scope="global" class="mbr-topup__terms">
          <template #terms>
            <button type="button" class="mbr-topup__terms-link" @click="showTerms = true">{{ $t('topup.terms') }}</button>
          </template>
        </i18n-t>
      </main>

      <!-- MBR123 get-profile / topup-list failed: no packages or balance to show -->
      <div v-if="loadFailed && !pageLoading" class="mbr-topup__error" role="alert">
        <span class="mbr-topup__error-icon" aria-hidden="true">
          <i class="material-icons-round">cloud_off</i>
        </span>
        <h2 class="mbr-topup__error-title">{{ $t('app.load_failed') }}</h2>
        <p class="mbr-topup__error-sub">{{ $t('legal.check_connection') }}</p>
        <material-button class="mbr-topup__error-retry" @click="loadPage()">{{ $t('legal.try_again') }}</material-button>
      </div>
    </div>

    <!-- MBR057 sticky pay bar -->
    <div class="mbr-topup__paybar" v-if="isPageReady">
      <div class="mbr-topup__paybar-inner">
        <div>
          <span class="mbr-topup__eyebrow">{{ $t('topup.you_pay') }}</span>
          <div class="mbr-topup__paybar-value">$ {{ formatMoney(payAmount) }}</div>
        </div>
        <button
          type="button"
          class="mbr-topup__cta"
          :disabled="!hasPackage || !currentMethod"
          @click="processPayment"
        >
          <span>{{ $t('topup.pay') }}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14m-6-6 6 6-6 6" />
          </svg>
        </button>
      </div>
    </div>

    <!-- MBR057 Terms & Conditions sheet -->
    <teleport to="body">
      <transition name="mbr-topup-sheet">
        <div v-if="showTerms" class="mbr-topup-sheet" @click.self="showTerms = false" @keydown.esc="showTerms = false">
          <div class="mbr-topup-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="mbr-topup-terms-title">
            <span class="mbr-topup-sheet__handle" aria-hidden="true"></span>
            <div class="mbr-topup-sheet__head">
              <h2 id="mbr-topup-terms-title" class="mbr-topup-sheet__title">{{ $t('topup.terms') }}</h2>
              <button type="button" class="mbr-topup-sheet__close" :aria-label="$t('common.close')" @click="showTerms = false">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="mbr-topup-sheet__content" v-html="terms_conditions"></div>
          </div>
        </div>
      </transition>
    </teleport>

    <div class="position-fixed top-1 end-1 z-index-2">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <TopupCompleted :processDone="processDone" :amount="paidAmount" />
    <material-loading :active="loaderActive || pageLoading" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import { mapMutations, mapActions } from "vuex";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialButton from "@/components/MaterialButton.vue";
import TopupCompleted from "@/components/TopupCompleted.vue";
import MbrWalletCard from "@/components/MbrWalletCard.vue";
import MemberService from "../services/member.service";
import StripeService from "../services/stripe.service";
import AirwallexService from "../services/airwallex.service";

// Payment modes mirror MintyBranch::PAYMENT_MODE_* on the API.
const PAYMENT_MODE_STRIPE = 1;
const PAYMENT_MODE_AIRWALLEX = 3;
import "bootstrap/dist/js/bootstrap.bundle";
import $ from "jquery";
import { localStorageName, getMerchantCode } from "@/config/utils";
import { errorMessage } from "@/utils/errorMessage";

export default {
  name: "topup",
  components: {
    MaterialSnackbar,
    MaterialButton,
    TopupCompleted,
    MaterialLoading,
    MbrWalletCard,
  },
  data() {
    return {
      prices: [],
      terms_conditions: '',
      currentPrice: 0,
      currentMethod: "card",
      merchantCode: "",
      memberInfo: {},
      cashbackAmount: 0,
      loaderActive: false,
      // MBR123: get-profile + topup-list; kept apart from loaderActive so the
      // Stripe/Airwallex return check can't show the page before both are back
      pageLoading: true,
      loadFailed: false,
      snackbar: "",
      message: "",
      // Default payment gateway for this merchant (1 = Stripe, 3 = Airwallex HPP).
      // Pulled from getTopupList → minty_mer_settings.default_payment_mode.
      defaultPaymentMode: PAYMENT_MODE_STRIPE,
      showTerms: false,
      // MBR126: opened from the Home Top-up button (?from=home) -> back goes to Home, else Account.
      // Kept through the Stripe/Airwallex round trip via the return URLs.
      fromHome: this.$route.query.from === "home",
    };
  },
  // draws its own header like the design: hide the top navbar, and keep the
  // sidenav as an off-screen drawer (same as MyTransactions)
  beforeMount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.add("mbr-sidenav-overlay");
    app.classList.remove("g-sidenav-pinned");
  },
  beforeUnmount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.remove("mbr-sidenav-overlay");
    if (window.innerWidth > 1200) {
      app.classList.add("g-sidenav-pinned");
    }
  },
  mounted() {
    const code = getMerchantCode();
    const isCheckMerchantConnectedStripe = localStorage.getItem(localStorageName("IS_CHECK_MERCHANT_CONNECTED_STRIPE"), 'false');
    this.merchantCode = code ? `/${code}` : "";
    if(isCheckMerchantConnectedStripe === 'false') {
      window.location.href = this.merchantCode+'/home'
    }
    //load after payment done
    var session_id = this.$route.query.session_id;
    var airwallex_intent_id = this.$route.query.airwallex_intent_id;
    var price = this.$route.query.price;

    if (session_id) { // call back after pay success - stripe
      this.loaderActive = true;
      StripeService.checkCheckoutSessionStripe({ stripe_id: session_id }, code).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.currentPrice = price;
            this.loadDonePayment();
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.loaderActive = false;
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.loaderActive = false;
        }
      );
    } else if (airwallex_intent_id) { // call back after pay success - Airwallex HPP
      this.loaderActive = true;
      AirwallexService.checkHppAirwallex(
        { payment_intent_id: airwallex_intent_id },
        code
      ).then(
        (response) => {
          // Checked once; drop the id so a reload doesn't show the result again.
          const query = { ...this.$route.query };
          delete query.airwallex_intent_id;
          this.$router.replace({ query });

          if (response.data.result.isSuccess) {
            this.currentPrice = price;
            this.loadDonePayment();
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.loaderActive = false;
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error, "topup.airwallex_check_failed");
          this.loaderActive = false;
        }
      );
    }

    this.loadPage();
  },
  computed: {
    isPageReady() {
      return !this.loaderActive && !this.pageLoading && !this.loadFailed;
    },
    // currentPrice is a package object once picked; after a Stripe/Airwallex
    // return it holds the paid amount from ?price= instead
    hasPackage() {
      return !!this.currentPrice && typeof this.currentPrice === "object";
    },
    payAmount() {
      return this.hasPackage ? this.currentPrice.payment_amount : 0;
    },
    paidAmount() {
      return String(this.hasPackage ? this.currentPrice.payment_amount : this.currentPrice || 0);
    },
  },
  methods: {
    ...mapMutations(["toggleNavbarOnly", "toggleHideConfig"]),
    ...mapActions(["setHeaderColor", "setLabelColor", "setButtonColor"]),
    selectPrice(index, value) {
      this.currentPrice = value;
    },
    formatMoney(value) {
      return new Intl.NumberFormat("en", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(value || 0);
    },
    // package cards drop ".00" like the design (S$120, S$2,070)
    formatShort(value) {
      return new Intl.NumberFormat("en", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      }).format(value || 0);
    },
    bonusOf(price) {
      return Math.round((Number(price.topup_amount) - Number(price.payment_amount)) * 100) / 100;
    },
    goBack() {
      this.$router.push(this.merchantCode + (this.fromHome ? "/home" : "/account"));
    },
    selectMethod(method) {
      this.currentMethod = method;
    },
    processDone() {
      $(".btn-topup_completed_modal").click();
        $(".btn-topup_completed_modal").click();

      this.$router.replace({
        path: this.$route.path,
        query: this.fromHome ? { from: "home" } : {}
      });

      this.loadPage();
    },
    processPayment() {
      this.loaderActive = true;
      const merchantCode = getMerchantCode();
      const url = location.protocol + "//" + location.host + "/" + merchantCode + "/top-up";
      const fromParam = this.fromHome ? "&from=home" : "";
      const payment_amount = new Intl.NumberFormat("en", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(this.currentPrice.payment_amount);

      // Common payload (same shape as Stripe; the BE knows how to read it).
      const basePayload = {
        cancel_url: url + (this.fromHome ? "?from=home" : ""),
        images: "",
        name: "TOP UP " + payment_amount,
        unit_amount: this.currentPrice.payment_amount,
        quantity: 1,
        isTopupOnMember: 1,
        topup_id: this.currentPrice.id,
        member_id: this.memberInfo.id,
      };

      if (this.defaultPaymentMode === PAYMENT_MODE_AIRWALLEX) {
        // Airwallex HPP — the BE creates a Payment Intent and returns the
        // hosted checkout URL. successUrl is appended with airwallex_intent_id
        // server-side so the return page can confirm via /payment/check-hpp.
        AirwallexService.checkoutHppAirwallex(
          {
            ...basePayload,
            success_url: url + "?price=" + this.currentPrice.payment_amount + "&id=" + this.currentPrice.id + fromParam,
          },
          merchantCode
        ).then(
          (response) => {
            if (response.data.result.isSuccess) {
              // Loader stays up while the SDK loads and navigates away.
              AirwallexService.redirectToHpp(response.data.data, () => {
                this.loaderActive = false;
              }).catch(() => {
                this.snackbar = "danger";
                this.message = this.$t("topup.airwallex_checkout_failed");
                this.setTimeOutNotification();
                this.loaderActive = false;
              });
            } else {
              this.snackbar = "danger";
              this.message = errorMessage(response.data.result.message);
              this.setTimeOutNotification();
              this.loaderActive = false;
            }
          },
          (error) => {
            this.snackbar = "danger";
            this.message = errorMessage(error, "topup.airwallex_checkout_failed");
            this.setTimeOutNotification();
            this.loaderActive = false;
          }
        );
      } else {
        // Default: Stripe Checkout Session (existing flow).
        StripeService.checkoutSessionStripe(
          {
            ...basePayload,
            success_url: url + "?session_id={CHECKOUT_SESSION_ID}&price=" + this.currentPrice.payment_amount + "&id=" + this.currentPrice.id + fromParam,
          },
          merchantCode
        ).then(
          (response) => {
            if (response.data.result.isSuccess) {
              window.location.href = response.data.data.url;
            } else {
              this.loaderActive = false;
            }
          },
          (error) => {
            console.log(error);
            this.loaderActive = false;
          }
        );
      }
    },
    loadDonePayment() {
      $(".btn-topup_completed_modal").click();
    },
    gotoTransaction() {
      this.$router.push(this.merchantCode + "/my-transactions");
    },
    // MBR123: both calls must succeed — a failed result has no data, and reading
    // it threw before the loader was turned off
    loadPage() {
      this.pageLoading = true;
      this.loadFailed = false;
      return Promise.all([this.getMemberInfo(), this.getTopupList()])
        .then(([profileOk, topupOk]) => {
          this.loadFailed = !(profileOk && topupOk);
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    getMemberInfo() {
      return MemberService.getProfile().then(
        (response) => {
          if (!response.data.result.isSuccess) {
            return false;
          }
          this.memberInfo = response.data.data;
          this.cashbackAmount = this.memberInfo.amount_in_account ?? 0;
          return true;
        },
        (error) => {
          console.log(error);
          return false;
        }
      );
    },
    getTopupList() {
      return MemberService.getTopupList().then(
        (response) => {
          if (!response.data.result.isSuccess) {
            return false;
          }
          this.prices = response.data.data.topup;
          this.terms_conditions = response.data.data.termsConditions;
          this.defaultPaymentMode = parseInt(
            response.data.data.default_payment_mode
          ) || PAYMENT_MODE_STRIPE;
          return true;
        },
        (error) => {
          console.log(error);
          return false;
        }
      );
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
  },
};
</script>
<style scoped>
/* MBR055 Top-up */
.mbr-topup.page-header {
  overflow: visible;
  overflow-x: clip;
  font-family: var(--mbr-font-body);
  color: var(--mbr-color-ink-headline);
}

/* custom.css still carries the legacy `* { font-family: Poppins }` */
.mbr-topup :where(:not(.material-icons-round)),
.mbr-topup-sheet :where(:not(.material-icons-round)) {
  font-family: var(--mbr-font-body);
}

.mbr-topup .mask {
  background: var(--mbr-color-canvas) !important;
  opacity: 1;
}

.mbr-topup .container {
  max-width: 440px;
  margin: 0 auto;
}

/* MBR056 header */
.mbr-topup__bar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 24px 20px 12px;
  background: var(--mbr-nav-bg);
  border-bottom: 1px solid rgba(var(--mbr-color-outline-rgb), 0.4);
  backdrop-filter: blur(var(--mbr-nav-blur));
  -webkit-backdrop-filter: blur(var(--mbr-nav-blur));
}

.mbr-topup__head {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.mbr-topup__back {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: var(--mbr-color-surface);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  color: var(--mbr-color-primary);
  transition: transform 0.15s ease;
}

.mbr-topup__back:active {
  transform: scale(0.95);
}

.mbr-topup__back svg {
  width: 16px;
  height: 16px;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-topup__title {
  margin: 0;
  font-family: var(--mbr-font-headline) !important;
  font-size: 20px;
  font-weight: 800;
  line-height: 28px;
  letter-spacing: -0.02em;
  color: var(--mbr-color-ink-strong);
}

.mbr-topup__history {
  padding: 4px 0;
  background: none;
  border: 0;
  font-size: 12px;
  font-weight: 700;
  color: var(--mbr-color-primary);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}

.mbr-topup__back:focus-visible,
.mbr-topup__history:focus-visible,
.mbr-topup__pkg:focus-visible,
.mbr-topup__terms-link:focus-visible,
.mbr-topup__cta:focus-visible {
  outline: 2px solid var(--mbr-color-primary);
  outline-offset: 2px;
}

.mbr-topup__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 20px 0;
}

.mbr-topup__eyebrow {
  display: block;
  margin: 0;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--mbr-color-ink-secondary);
}

.mbr-topup__section {
  padding-top: 4px;
  font-size: 11px;
  letter-spacing: 0.05em;
}

/* MBR056 package grid */
.mbr-topup__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.mbr-topup__pkg {
  position: relative;
  /* MBR109 lets the "Get S$…" title size itself to the card */
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  padding: 16px;
  background: var(--mbr-color-card);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-lg);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  text-align: left;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

/* 2px border would shift content: compensate the padding */
.mbr-topup__pkg--on {
  padding: 15px;
  background: rgba(var(--mbr-color-palepink-rgb), 0.4);
  border: 2px solid var(--mbr-color-primary);
}

.mbr-topup__check {
  position: absolute;
  top: -10px;
  right: 12px;
  display: flex;
  padding: 4px;
  border-radius: var(--mbr-radius-full);
  background: var(--mbr-color-primary);
  box-shadow: 0 1px 3px rgba(var(--mbr-color-primary-rgb), 0.25);
  color: var(--mbr-color-on-primary);
}

.mbr-topup__check svg {
  width: 14px;
  height: 14px;
}

.mbr-topup__get {
  max-width: 100%;
  margin-bottom: 4px;
  font-family: var(--mbr-font-headline) !important;
  /* MBR109 always one line: scale with the card, ellipsis only as a last resort */
  font-size: 18px;
  font-size: clamp(12px, 12cqi, 22px);
  font-weight: 800;
  line-height: 1;
  color: var(--mbr-color-ink-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mbr-topup__pay {
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-topup__bonus {
  display: inline-block;
  padding: 2px 8px;
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
  border-radius: var(--mbr-radius-full);
  background: var(--mbr-color-peach);
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-transform: uppercase;
  color: var(--mbr-color-primary);
}

/* MBR057 consent line */
.mbr-topup__terms {
  margin: 0;
  padding-top: 8px;
  font-size: 11px;
  line-height: 16px;
  text-align: center;
  color: rgba(var(--mbr-color-ink-secondary-rgb), 0.75);
}

.mbr-topup__terms-link {
  padding: 0;
  background: none;
  border: 0;
  font-weight: 600;
  color: var(--mbr-color-primary);
  text-decoration: underline;
}

/* MBR057 sticky pay bar */
.mbr-topup__paybar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1030;
  background: rgba(var(--mbr-color-canvas-rgb), 0.95);
  border-top: 1px solid rgba(var(--mbr-color-outline-rgb), 0.8);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.mbr-topup__paybar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  max-width: 440px;
  margin: 0 auto;
  padding: 14px 20px calc(24px + env(safe-area-inset-bottom));
}

.mbr-topup__paybar-value {
  font-family: var(--mbr-font-headline) !important;
  font-size: 20px;
  font-weight: 800;
  line-height: 28px;
  color: var(--mbr-color-primary);
}

.mbr-topup__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  padding: 0 32px;
  border: 0;
  border-radius: var(--mbr-radius-full);
  background: var(--mbr-color-primary);
  box-shadow: 0 8px 20px -4px rgba(var(--mbr-color-primary-rgb), 0.35);
  font-family: var(--mbr-font-headline) !important;
  font-size: 14px;
  font-weight: 700;
  color: var(--mbr-color-on-primary);
  transition: transform 0.15s ease, background-color 0.15s ease;
}

.mbr-topup__cta:hover:not(:disabled) {
  background: var(--mbr-color-primary-light);
}

.mbr-topup__cta:active:not(:disabled) {
  transform: scale(0.95);
}

.mbr-topup__cta:disabled {
  opacity: 0.4;
  box-shadow: none;
}

/* the body-font reset above would otherwise reach the label */
.mbr-topup__cta span {
  font-family: var(--mbr-font-headline);
}

.mbr-topup__cta svg {
  width: 16px;
  height: 16px;
}

/* MBR057 T&C sheet (teleported to body) */
.mbr-topup-sheet {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(var(--mbr-color-ink-headline-rgb), 0.45);
}

.mbr-topup-sheet__panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 440px;
  max-height: 85vh;
  background: var(--mbr-color-surface);
  border-radius: 32px 32px 0 0;
  box-shadow: var(--mbr-shadow-modal);
  color: var(--mbr-color-ink-headline);
}

.mbr-topup-sheet__handle {
  display: block;
  flex: none;
  width: 40px;
  height: 4px;
  margin: 12px auto 0;
  border-radius: var(--mbr-radius-full);
  background: var(--mbr-color-outline);
}

.mbr-topup-sheet__head {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px 12px 24px;
}

.mbr-topup-sheet__title {
  margin: 0;
  font-family: var(--mbr-font-headline) !important;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  color: var(--mbr-color-ink-strong);
}

.mbr-topup-sheet__close {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: var(--mbr-radius-full);
  background: var(--mbr-color-tint);
  color: var(--mbr-color-ink-headline);
}

.mbr-topup-sheet__close svg {
  width: 20px;
  height: 20px;
}

.mbr-topup-sheet__content {
  overflow-y: auto;
  padding: 0 24px calc(32px + env(safe-area-inset-bottom));
  font-size: 14px;
  line-height: 22px;
  color: var(--mbr-color-ink-secondary);
  overflow-wrap: anywhere;
}

.mbr-topup-sheet__content :deep(p:last-child) {
  margin-bottom: 0;
}

.mbr-topup-sheet-enter-active,
.mbr-topup-sheet-leave-active {
  transition: opacity 0.25s ease;
}

.mbr-topup-sheet-enter-active .mbr-topup-sheet__panel,
.mbr-topup-sheet-leave-active .mbr-topup-sheet__panel {
  transition: transform 0.3s ease-out;
}

.mbr-topup-sheet-enter-from,
.mbr-topup-sheet-leave-to {
  opacity: 0;
}

.mbr-topup-sheet-enter-from .mbr-topup-sheet__panel,
.mbr-topup-sheet-leave-to .mbr-topup-sheet__panel {
  transform: translateY(100%);
}
/* MBR123 load error — same look as the Home (MBR121) / Activity (MBR122) error state */
.mbr-topup__error {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: calc(100vh - 200px);
  padding: 40px 16px 24px;
  text-align: center;
}

.mbr-topup__error-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 8px;
  background: var(--mbr-color-tint);
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-primary);
}

.mbr-topup__error-icon .material-icons-round {
  font-size: 32px;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-topup__error-title {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: var(--mbr-headline-sm-size);
  font-weight: 700;
  line-height: var(--mbr-headline-sm-line);
  color: var(--mbr-color-ink-headline);
}

.mbr-topup__error-sub {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--mbr-color-ink-muted);
}

.mbr-topup__error-retry {
  min-width: 160px;
  margin-top: 12px;
}
</style>
